import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FiSearch,
  FiCalendar, 
  FiPlus,
  FiTrendingUp,
  FiCheckCircle,
  FiHeart,
  FiEye,
  FiStar,
  FiZap,
  FiTrash2
} from 'react-icons/fi';
import { 
  FaRegLightbulb,
  FaRocket
} from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';

// API Base URL - Update this to your backend URL
const API_BASE_URL = "https://d1yeguxcjnjxm8.cloudfront.net/api/v1";

// Header Component with gradient
const Header = () => {
  return (
    <header className="bg-gradient-to-r from-[#0f2027] via-[#203a43] to-[#2c5364] shadow-xl sticky top-0 z-50">
      <div className="max-w-[1400px] mx-auto px-6 h-20 flex items-center justify-between">
        {/* Logo with improved placement */}
        <div className="flex items-center h-full ml-14 mr-6">
          <Link to="/" className="flex items-center no-underline text-inherit">
            <img
              src="/logo_mycrmsim.webp"
              alt="MyCRMSIM Logo"
              className="h-6 md:h-8 w-auto object-contain"
            />
          </Link>
        </div>

        {/* Navigation Links */}
        <nav className="flex-1 mx-10">
          <div className="flex gap-7">
            <Link to="/changelog" className="flex items-center gap-2 no-underline text-white text-sm font-semibold py-3 px-5 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 shadow-lg hover:shadow-emerald-500/30 transition-all duration-300">
              <FaRocket className="text-base" />
              <span>What's New</span>
            </Link>
          </div>
        </nav>
      </div>
    </header>
  );
};

// Helper function to get icon based on type
const getTypeIcon = (type) => {
  switch(type?.toUpperCase()) {
    case 'ADDED':
    case 'NEW':
      return <FiPlus className="text-emerald-500" />;
    case 'IMPROVED':
    case 'UPDATED':
    case 'ENHANCED':
      return <FiTrendingUp className="text-blue-500" />;
    case 'FIXED':
      return <FiCheckCircle className="text-orange-500" />;
    case 'REMOVED':
    case 'DELETED':
      return <FiTrash2 className="text-red-500" />;
    default:
      return <FaRegLightbulb className="text-purple-500" />;
  }
};

// Helper function to format date
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
};

// Helper function to get relative time
const getRelativeTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = Math.abs(now - date);
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return `${Math.floor(diffDays / 365)} years ago`;
};

const DocumentationPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [changelogData, setChangelogData] = useState([]);
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedType, setSelectedType] = useState('all');
  
  // Load viewed and liked IDs from localStorage (persists across sessions)
  const [viewedIds, setViewedIds] = useState(() => {
    const saved = localStorage.getItem('changelog_viewed_ids');
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });
  
  const [likedIds, setLikedIds] = useState(() => {
    const saved = localStorage.getItem('changelog_liked_ids');
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });

  // Save to localStorage when viewedIds changes
  useEffect(() => {
    localStorage.setItem('changelog_viewed_ids', JSON.stringify([...viewedIds]));
  }, [viewedIds]);

  // Save to localStorage when likedIds changes
  useEffect(() => {
    localStorage.setItem('changelog_liked_ids', JSON.stringify([...likedIds]));
  }, [likedIds]);

  

  // Function to like/unlike (toggle)
  const handleLike = async (id, e) => {
    e.stopPropagation();
    const isLiked = likedIds.has(id);
    const endpoint = isLiked ? 'unlike' : 'like';
    const likeChange = isLiked ? -1 : 1;
    
    try {
      await fetch(`${API_BASE_URL}/changelog/${id}/${endpoint}`, {
        method: 'PATCH',
      });
      
      setLikedIds(prev => {
        const newSet = new Set([...prev]);
        if (isLiked) {
          newSet.delete(id);
        } else {
          newSet.add(id);
        }
        return newSet;
      });
      
      setChangelogData(prevData => 
        prevData.map(item => 
          item.id === id ? { ...item, likes: Math.max(0, (item.likes || 0) + likeChange) } : item
        )
      );
      setFilteredEntries(prevData => 
        prevData.map(item => 
          item.id === id ? { ...item, likes: Math.max(0, (item.likes || 0) + likeChange) } : item
        )
      );
    } catch (err) {
      console.error('Error toggling like:', err);
    }
  };

  // Fetch changelogs from API
  useEffect(() => {
    const fetchChangelogs = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetch(`${API_BASE_URL}/changelog`);
        const result = await response.json();
        
        if (result.success && result.data) {
          setChangelogData(result.data);
          setFilteredEntries(result.data);
          
          // Increment view for all visible items
          const savedViewed = localStorage.getItem('changelog_viewed_ids');
          const viewedSet = savedViewed ? new Set(JSON.parse(savedViewed)) : new Set();
          
          result.data.forEach(item => {
            if (item.id && !viewedSet.has(item.id)) {
              fetch(`${API_BASE_URL}/changelog/${item.id}/view`, { method: 'PATCH' })
                .catch(err => console.error('Error auto-view:', err));
              viewedSet.add(item.id);
            }
          });
          
          localStorage.setItem('changelog_viewed_ids', JSON.stringify([...viewedSet]));
          setViewedIds(new Set(viewedSet));
        } else {
          setError(result.message || 'Failed to fetch changelogs');
        }
      } catch (err) {
        console.error('Error fetching changelogs:', err);
        setError('Failed to connect to the server. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchChangelogs();
  }, []);

  // Filter entries based on search query and type
  useEffect(() => {
    let filtered = changelogData;
    
    // Filter by type
    if (selectedType !== 'all') {
      filtered = filtered.filter(item => 
        item.type?.toUpperCase() === selectedType.toUpperCase()
      );
    }
    
    // Filter by search query
    if (searchQuery.trim() !== '') {
      filtered = filtered.filter(item => 
        item.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.version?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    setFilteredEntries(filtered);
  }, [searchQuery, changelogData, selectedType]);

  const getBadgeStyle = (type) => {
    const typeUpper = type?.toUpperCase();
    switch(typeUpper) {
      case 'ADDED':
      case 'NEW':
        return 'bg-gradient-to-r from-emerald-500 to-green-500 text-white shadow-lg shadow-emerald-500/30';
      case 'UPDATED':
      case 'IMPROVED':
      case 'ENHANCED':
        return 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg shadow-blue-500/30';
      case 'FIXED':
        return 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg shadow-orange-500/30';
      case 'REMOVED':
      case 'DELETED':
        return 'bg-gradient-to-r from-red-500 to-rose-500 text-white shadow-lg shadow-red-500/30';
      default:
        return 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg shadow-purple-500/30';
    }
  };

  const getBadgeLabel = (type) => {
    const typeUpper = type?.toUpperCase();
    switch(typeUpper) {
      case 'ADDED':
      case 'NEW':
        return 'ADDED';
      case 'UPDATED':
      case 'IMPROVED':
      case 'ENHANCED':
        return 'UPDATED';
      case 'FIXED':
        return 'FIXED';
      case 'REMOVED':
      case 'DELETED':
        return 'REMOVED';
      default:
        return type?.toUpperCase() || 'OTHER';
    }
  };

  const getCardGradient = (type) => {
    const typeUpper = type?.toUpperCase();
    switch(typeUpper) {
      case 'ADDED':
      case 'NEW':
        return 'hover:border-emerald-300 hover:shadow-emerald-100';
      case 'UPDATED':
      case 'IMPROVED':
      case 'ENHANCED':
        return 'hover:border-blue-300 hover:shadow-blue-100';
      case 'FIXED':
        return 'hover:border-orange-300 hover:shadow-orange-100';
      case 'REMOVED':
      case 'DELETED':
        return 'hover:border-red-300 hover:shadow-red-100';
      default:
        return 'hover:border-purple-300 hover:shadow-purple-100';
    }
  };

  const filterButtons = [
    { key: 'all', label: 'All', icon: <FiStar /> },
    { key: 'added', label: 'Added', icon: <FiPlus /> },
    { key: 'updated', label: 'Updated', icon: <FiTrendingUp /> },
    { key: 'fixed', label: 'Fixed', icon: <FiCheckCircle /> },
    { key: 'removed', label: 'Removed', icon: <FiTrash2 /> },
  ];

  return (
    <div className="min-h-screen flex flex-col font-sans bg-gradient-to-br from-slate-50 via-white to-slate-100">
      <Header />
      
      <main className="flex-1 max-w-6xl mx-auto w-full px-6 py-12 relative">
        {/* Hero Section */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-violet-100 to-purple-100 rounded-full text-purple-700 text-sm font-medium mb-4">
            <FiZap className="text-purple-500" />
            Latest Product Updates
          </div>
          <h1 className="text-5xl font-bold bg-gradient-to-r from-slate-900 via-purple-900 to-slate-900 bg-clip-text text-transparent mb-4">
            What's New
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Discover the latest features, improvements, and fixes we've shipped to make your experience even better.
          </p>
        </motion.div>

        {/* Search and Filter Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mb-10"
        >
          {/* Search Bar */}
          <div className="relative mb-6">
            <FiSearch className="absolute left-5 top-1/2 transform -translate-y-1/2 text-slate-400 text-xl" />
            <input
              type="text"
              placeholder="Search updates..."
              className="w-full pl-14 pr-6 py-4 bg-white border-2 border-slate-200 rounded-2xl focus:outline-none focus:border-purple-400 focus:ring-4 focus:ring-purple-100 text-lg transition-all duration-300 shadow-sm"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Filter Buttons */}
          <div className="flex flex-wrap justify-center gap-3">
            {filterButtons.map((btn) => (
              <button
                key={btn.key}
                onClick={() => setSelectedType(btn.key)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 ${
                  selectedType === btn.key
                    ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg shadow-purple-500/30 scale-105'
                    : 'bg-white text-slate-600 border-2 border-slate-200 hover:border-purple-300 hover:text-purple-600 hover:shadow-md'
                }`}
              >
                {btn.icon}
                {btn.label}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Loading Indicator */}
        <AnimatePresence>
          {isLoading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center justify-center py-20"
            >
              <div className="relative">
                <div className="w-16 h-16 border-4 border-purple-200 rounded-full"></div>
                <div className="w-16 h-16 border-4 border-purple-600 border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
              </div>
              <p className="mt-4 text-slate-500 font-medium">Loading updates...</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Error State */}
        {error && (
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex flex-col items-center justify-center py-20 px-8 bg-red-50 rounded-3xl border-2 border-red-200"
          >
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <FiZap className="text-3xl text-red-500" />
            </div>
            <h3 className="text-xl font-semibold text-red-700 mb-2">Oops! Something went wrong</h3>
            <p className="text-red-600 mb-6">{error}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="px-6 py-3 bg-gradient-to-r from-red-500 to-rose-500 text-white rounded-xl font-semibold shadow-lg shadow-red-500/30 hover:shadow-xl hover:shadow-red-500/40 transition-all duration-300"
            >
              Try Again
            </button>
          </motion.div>
        )}

        {/* Changelog Cards */}
        {!isLoading && !error && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="space-y-6"
          >
            {filteredEntries.length > 0 ? (
              filteredEntries.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  whileHover={{ y: -4 }}
                  className={`group relative bg-white rounded-3xl border-2 border-slate-200 p-8 shadow-sm hover:shadow-2xl transition-all duration-500 ${getCardGradient(item.type)}`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                    {/* Icon Circle */}
                    <div className={`flex-shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center text-2xl ${
                      item.type?.toUpperCase() === 'ADDED' || item.type?.toUpperCase() === 'NEW' ? 'bg-emerald-100' :
                      item.type?.toUpperCase() === 'UPDATED' || item.type?.toUpperCase() === 'IMPROVED' ? 'bg-blue-100' :
                      item.type?.toUpperCase() === 'FIXED' ? 'bg-orange-100' :
                      item.type?.toUpperCase() === 'REMOVED' ? 'bg-red-100' : 'bg-purple-100'
                    }`}>
                      {getTypeIcon(item.type)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-3 mb-3">
                        <span className={`inline-flex items-center px-4 py-1.5 text-xs font-bold rounded-full ${getBadgeStyle(item.type)}`}>
                          {getBadgeLabel(item.type)}
                        </span>
                        {item.version && (
                          <span className="inline-flex items-center px-3 py-1.5 bg-slate-100 text-slate-600 text-xs font-semibold rounded-full">
                            v{item.version}
                          </span>
                        )}
                        <span className="flex items-center gap-1.5 text-sm text-slate-400">
                          <FiCalendar className="text-slate-300" />
                          {formatDate(item.created_at)} • {getRelativeTime(item.created_at)}
                        </span>
                      </div>

                      <h3 className="text-2xl font-bold text-slate-900 mb-3 group-hover:text-purple-700 transition-colors duration-300">
                        {item.title}
                      </h3>

                      {item.description && (
                        <div 
                          className="text-slate-600 leading-relaxed prose prose-sm max-w-none changelog-content"
                          dangerouslySetInnerHTML={{ __html: item.description }}
                        />
                      )}
                    </div>

                    {/* Stats */}
                    <div className="flex lg:flex-col items-center gap-4 lg:gap-3">
                      <button 
                        onClick={(e) => handleLike(item.id, e)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all duration-300 ${
                          likedIds.has(item.id) 
                            ? 'bg-gradient-to-r from-pink-500 to-rose-500 text-white shadow-lg shadow-pink-500/30' 
                            : 'bg-slate-100 text-slate-500 hover:bg-pink-100 hover:text-pink-600'
                        }`}
                        title={likedIds.has(item.id) ? 'Click to unlike' : 'Like this update'}
                      >
                        <FiHeart className={likedIds.has(item.id) ? 'fill-current' : ''} />
                        <span className="font-semibold">{item.likes || 0}</span>
                      </button>
                      <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-500 rounded-xl">
                        <FiEye />
                        <span className="font-semibold">{item.views || 0}</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex flex-col items-center justify-center py-20 px-8 bg-gradient-to-br from-slate-50 to-purple-50 rounded-3xl border-2 border-dashed border-slate-300"
              >
                <div className="w-20 h-20 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center mb-6">
                  <FiSearch className="text-3xl text-purple-400" />
                </div>
                <h3 className="text-xl font-semibold text-slate-700 mb-2">No updates found</h3>
                <p className="text-slate-500 text-center max-w-md">
                  {searchQuery ? `No results for "${searchQuery}". Try a different search term.` : 'Check back later for new updates!'}
                </p>
                {searchQuery && (
                  <button 
                    onClick={() => setSearchQuery('')}
                    className="mt-6 px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-semibold shadow-lg shadow-purple-500/30 hover:shadow-xl hover:shadow-purple-500/40 transition-all duration-300"
                  >
                    Clear Search
                  </button>
                )}
              </motion.div>
            )}
          </motion.div>
        )}

        {/* Stats Footer */}
        {!isLoading && !error && filteredEntries.length > 0 && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mt-12 flex justify-center"
          >
            <div className="inline-flex items-center gap-6 px-8 py-4 bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl text-white shadow-xl">
              <div className="flex items-center gap-2">
                <FiZap className="text-amber-400" />
                <span className="font-semibold">{filteredEntries.length}</span>
                <span className="text-slate-400">Updates</span>
              </div>
              <div className="w-px h-6 bg-slate-600"></div>
              <div className="flex items-center gap-2">
                <FiHeart className="text-pink-400" />
                <span className="font-semibold">{filteredEntries.reduce((acc, item) => acc + (item.likes || 0), 0)}</span>
                <span className="text-slate-400">Likes</span>
              </div>
              <div className="w-px h-6 bg-slate-600"></div>
              <div className="flex items-center gap-2">
                <FiEye className="text-blue-400" />
                <span className="font-semibold">{filteredEntries.reduce((acc, item) => acc + (item.views || 0), 0)}</span>
                <span className="text-slate-400">Views</span>
              </div>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default DocumentationPage;
